import { useCallback, useEffect, useState } from 'react';

import type { WidgetOnlineWeighingProps } from '../components/dashboard/Dashboard.types';
import {
	isDashboardWidgetsKey,
	readStoredWidgets,
} from './dashboardWidgetsStorage';

// Fired by useDashboard on persist so the navbar feed updates same-tab
// (the native 'storage' event only fires in *other* tabs).
export const WIDGETS_CHANGED_EVENT = 'dashboard-widgets-changed';

export const SCALE_WIDGET_TYPES = [
	'onlineWeighing',
	'onlineWeighingV2',
] as const;

const readScaleWidgets = (): WidgetOnlineWeighingProps[] =>
	(readStoredWidgets() as WidgetOnlineWeighingProps[]).filter(
		(w) =>
			(SCALE_WIDGET_TYPES as readonly string[]).includes(w?.type) &&
			w?.scale?.Id != null,
	);

/**
 * Online-weighing (V1 and V2) dashboard widgets the user has, kept in sync with storage.
 * Pass `enabled = false` on pages that never use this (e.g. row menus outside
 * the orders table) to skip the initial localStorage read and the listeners.
 */
const useScaleWidgets = (enabled = true): WidgetOnlineWeighingProps[] => {
	const [widgets, setWidgets] = useState<WidgetOnlineWeighingProps[]>(() =>
		enabled ? readScaleWidgets() : [],
	);

	const refresh = useCallback(() => setWidgets(readScaleWidgets()), []);

	useEffect(() => {
		if (!enabled) return;
		const onStorage = (e: StorageEvent) => {
			if (isDashboardWidgetsKey(e.key)) refresh();
		};
		window.addEventListener('storage', onStorage);
		window.addEventListener(WIDGETS_CHANGED_EVENT, refresh);
		return () => {
			window.removeEventListener('storage', onStorage);
			window.removeEventListener(WIDGETS_CHANGED_EVENT, refresh);
		};
	}, [enabled, refresh]);

	return widgets;
};

export default useScaleWidgets;
